import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { TreatmentRequestsIcon } from '@strata/react-chat';
import { FormSectionWithRows } from '@strata/react-form-section';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { Icon } from '@strata/react-status-icons';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import { CopayAssistanceMessageDialog } from './dialogs/CopayAssistanceMessageDialog';

import Copay from 'assets/copay.svg?react';
import { useNeedsByDate } from 'services/needsByDateHelpers';
import { useGetCompassUserNameMapQuery } from 'store/api/authApi';
import { useGetShippingInfoQuery } from 'store/api/onboardingApi';
import { useGetRequestsForTreatmentQuery } from 'store/api/payerRequestApi';

const mapToDetailLabelValuePair = ({ label, value }, index) => (
    <DetailLabelValuePair
        key={label}
        style={{ width: (index + 1) % 3 ? '33%' : '34%' }}
        label={label}
        value={value}
    />
);

// eslint-disable-next-line complexity
export const ReferralDetails = ({ referralDetails = undefined }) => {
    const [showCopayAssistanceMessageDialog, setShowCopayAssistanceMessageDialog] = useState(false);
    const { needsByDateData } = useNeedsByDate({
        payerId: referralDetails?.payerId,
        treatmentId: referralDetails?.treatmentId,
        referralId: referralDetails?.referralId,
    });

    const { data: treatmentRequestResponse } = useGetRequestsForTreatmentQuery(
        { payerId: referralDetails?.payerId, treatmentId: referralDetails?.treatmentId },
        { skip: !referralDetails },
    );
    const { data: userNameMap } = useGetCompassUserNameMapQuery();
    const treatmentRequests = useMemo(
        () =>
            treatmentRequestResponse?.map((request) => ({
                ...request,
                assignedFullName: userNameMap?.[request.assignedUpn],
            })),
        [treatmentRequestResponse, userNameMap],
    );
    const { data: shippingInfo } = useGetShippingInfoQuery(
        !referralDetails
            ? skipToken
            : {
                  referralId: referralDetails?.referralId,
              },
    );

    const detailLabelValuePairs = [
        {
            label: 'Received Date',
            value: formatDate(referralDetails?.receivedTime, DateFormats.DATE_TIME),
        },
        {
            label: 'Outreach Start Date',
            value: formatDate(referralDetails?.outreachDate),
        },
        {
            label: 'Servicing Start Date',
            value: formatDate(referralDetails?.referralStart),
        },
        {
            label: 'Referral Expiration',
            value: referralDetails?.originalReferralExpiration ? (
                <>
                    <div>{formatDate(referralDetails.originalReferralExpiration)} (original)</div>
                    <div>{formatDate(referralDetails.referralExpiration)} (actual)</div>
                </>
            ) : (
                formatDate(referralDetails?.referralExpiration)
            ),
        },
        {
            label: 'Product Type',
            value: referralDetails?.isBrand ? 'Brand' : 'Generic',
        },
        {
            label: 'Next Fill Needs By Date',
            value: formatDate(needsByDateData?.needsByDate),
        },
        {
            label: 'Place of Service',
            value: referralDetails?.placeOfService,
        },
        {
            label: 'NDC',
            value: referralDetails?.productServiceId,
        },
        {
            label: 'Qty',
            value: referralDetails?.productQty,
        },
        {
            label: 'Last Tracking Number',
            value: shippingInfo?.tracking_number,
        },
        {
            label: 'Shipment Date',
            value: formatDate(shippingInfo?.shipment_date),
        },
        {
            label: 'Shipment Carrier',
            value: shippingInfo?.shipment_carrier,
        },
        {
            label: 'Payer Requests',
            value:
                treatmentRequests?.length > 0 ? (
                    <TreatmentRequestsIcon requests={treatmentRequests} pbmOnlyAccess />
                ) : undefined,
        },
        {
            label: 'Copay Program',
            value: referralDetails?.copayAssistanceMessage ? (
                <Icon
                    icon={
                        <Copay
                            style={{
                                width: '16px',
                                height: '16px',
                            }}
                        />
                    }
                    size={12}
                    hoverCloseDelay={1000}
                    tooltipContent={
                        <span>
                            Copay assistance available.{' '}
                            <u
                                role="button"
                                style={{
                                    cursor: 'pointer',
                                }}
                                tabIndex={0}
                                onClick={() => setShowCopayAssistanceMessageDialog(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setShowCopayAssistanceMessageDialog(true);
                                    }
                                }}
                            >
                                See Details
                            </u>
                        </span>
                    }
                />
            ) : undefined,
        },
    ];
    const pairsToShow = detailLabelValuePairs.map((label, value) =>
        mapToDetailLabelValuePair(label, value),
    );
    const getThreePairs = (row) => {
        const zeroBasedIndex = row - 1;
        const rowStartIndex = zeroBasedIndex * 3;
        const rowEndIndex = row * 3;
        // If first index in row exists, then return that row
        if (pairsToShow[rowStartIndex]) {
            return pairsToShow.slice(rowStartIndex, rowEndIndex);
        }
        // Else, nothing to show for that row
        return null;
    };

    return (
        <>
            <CopayAssistanceMessageDialog
                isOpen={showCopayAssistanceMessageDialog}
                copayAssistanceMessage={referralDetails?.copayAssistanceMessage}
                onSecondaryButtonClick={() => setShowCopayAssistanceMessageDialog(false)}
            />
            <FormSectionWithRows
                sectionTitle="Referral Details"
                isLoading={!referralDetails?.referralId}
                width="100%"
                rowOneContent={getThreePairs(1)}
                rowTwoContent={getThreePairs(2)}
                rowThreeContent={getThreePairs(3)}
                rowFourContent={getThreePairs(4)}
                rowFiveContent={getThreePairs(5)}
                rowSixContent={getThreePairs(6)}
            />
        </>
    );
};

ReferralDetails.propTypes = {
    referralDetails: PropTypes.shape({
        receivedTime: PropTypes.string,
        referralExpiration: PropTypes.string,
        originalReferralExpiration: PropTypes.string,
        referralStart: PropTypes.string,
        outreachDate: PropTypes.string,
        submittedBy: PropTypes.string,
        placeOfService: PropTypes.string,
        specialInstructions: PropTypes.string,
        isRta: PropTypes.bool,
        referralId: PropTypes.string,
        productServiceId: PropTypes.string,
        productQty: PropTypes.number,
        isBrand: PropTypes.bool,
        treatmentId: PropTypes.string,
        payerId: PropTypes.string,
        copayAssistanceMessage: PropTypes.shape({}),
    }),
};
